/**
 * AzureImportPicker — bridges the "Azure (signed-in account)" import source to
 * the shared `AzureResourcePicker`. Azure has no on-disk store to scan, so
 * choosing it opens this multi-select picker (no engine filter); confirming a
 * pick maps the chosen resources to import candidates and hands them to the
 * framework's normal review dialog (`openFromScan`).
 *
 * Discovery is reused from the picker itself: this component and the inner
 * `AzureResourcePicker` both read `useAzureDiscovery` with the same identity
 * key, so TanStack Query serves one shared result. Candidates already saved as
 * connections are passed as `disabledResourceIds` so the picker greys them out
 * (the review dialog also pre-unchecks any that slip through). Rendered once,
 * near `ImportConnectionsDialog`, inside the app's QueryClientProvider.
 */
import { useMemo } from 'react';
import type { AzureDiscoveredResource } from '../../../types/azureDiscovery';
import { AzureResourcePicker, useAzurePickerStore } from '../azure-picker';
import { useAzureDiscovery } from '../azure-picker/useAzureDiscovery';
import { useConnectionStore } from '../store/connectionStore';
import { useConnectionImportStore } from './connectionImportStore';
import { mapAzureResourceToCandidate } from './azureImportMapping';
import { openAzureReview } from './connectionImportService';
import { isDuplicateCandidate } from './connectionImportUtils';

export function AzureImportPicker() {
  const azureOpen = useConnectionImportStore((s) => s.azureOpen);
  // Mount the discovery hooks only while the picker is open (keeps the query
  // idle otherwise and matches AzureBrowseButton's mount-while-open pattern).
  if (!azureOpen) return null;
  return <AzureImportPickerInner />;
}

function AzureImportPickerInner() {
  const azureEntry = useConnectionImportStore((s) => s.azureEntry);
  const closeAzurePicker = useConnectionImportStore((s) => s.closeAzurePicker);
  const connections = useConnectionStore((s) => s.connections);
  const preferredIdentity = useAzurePickerStore((s) => s.preferredIdentity);

  // Same query key as the inner picker → one shared discovery, not two.
  const { data } = useAzureDiscovery({ open: true, source: preferredIdentity });
  const resources = useMemo(() => (data?.ok ? data.resources : []), [data]);

  const disabledResourceIds = useMemo(
    () =>
      resources
        .map(mapAzureResourceToCandidate)
        .filter((candidate) => isDuplicateCandidate(candidate, connections))
        .map((candidate) => candidate.key),
    [resources, connections]
  );

  const handlePickMany = (picked: AzureDiscoveredResource[]) => {
    closeAzurePicker();
    openAzureReview(picked, azureEntry);
  };

  return (
    <AzureResourcePicker
      open
      onOpenChange={(open) => {
        if (!open) closeAzurePicker();
      }}
      multiSelect
      entry="import"
      disabledResourceIds={disabledResourceIds}
      onPickMany={handlePickMany}
    />
  );
}
